// src/components/PWAUpdatePrompt.jsx
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, X } from 'lucide-react'
import Button from './ui/Button'

// How often a running client asks the server whether a newer build is deployed
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000

// Baked in at build time by vite.config.js; the server serves the deployed
// build's value at /version.json
const CURRENT_VERSION = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev'

const PWAUpdatePrompt = () => {
    const [showUpdatePrompt, setShowUpdatePrompt] = useState(false)
    const [waitingWorker, setWaitingWorker] = useState(null)

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                window.location.reload()
            })

            navigator.serviceWorker.ready.then((registration) => {
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration.installing
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                setWaitingWorker(newWorker)
                                setShowUpdatePrompt(true)
                            }
                        })
                    }
                })
            })
        }
    }, [])

    // Server-triggered updates: compare our baked-in build version against the
    // one the server is serving. Runs on load, when the app returns to the
    // foreground, and every few minutes - so a redeploy reaches open clients
    // without anyone tapping refresh.
    useEffect(() => {
        if (CURRENT_VERSION === 'dev') return

        let cancelled = false

        const checkVersion = async () => {
            try {
                const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
                if (!res.ok) return
                const data = await res.json()
                if (cancelled || !data.version || data.version === CURRENT_VERSION) return

                // Guard against reload loops if the served version stays mismatched
                const guardKey = 'baraqubot-reloaded-for'
                if (sessionStorage.getItem(guardKey) === data.version) return
                sessionStorage.setItem(guardKey, data.version)

                const registration = await navigator.serviceWorker?.getRegistration()
                if (registration) {
                    // New SW installs, auto-activates (autoUpdate mode), and the
                    // controllerchange listener above reloads the page
                    await registration.update()
                    // Fallback if controllerchange never fires (e.g. SW disabled)
                    setTimeout(() => window.location.reload(), 4000)
                } else {
                    window.location.reload()
                }
            } catch {
                // offline or server restarting - try again next cycle
            }
        }

        const onVisible = () => {
            if (document.visibilityState === 'visible') checkVersion()
        }

        checkVersion()
        document.addEventListener('visibilitychange', onVisible)
        const timer = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS)
        return () => {
            cancelled = true
            document.removeEventListener('visibilitychange', onVisible)
            clearInterval(timer)
        }
    }, [])

    const handleUpdate = () => {
        if (waitingWorker) {
            waitingWorker.postMessage({ type: 'SKIP_WAITING' })
            setShowUpdatePrompt(false)
        }
    }

    const handleDismiss = () => {
        setShowUpdatePrompt(false)
    }

    return (
        <AnimatePresence>
            {showUpdatePrompt && (
                <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 100 }}
                    className="fixed bottom-4 left-4 right-4 z-50 max-w-sm mx-auto"
                >
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <Download className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                    App Update Available
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    A new version of BaraquBot is ready. Update now for the latest features.
                                </p>
                            </div>
                            <button
                                onClick={handleDismiss}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="mt-3 flex space-x-2">
                            <Button
                                onClick={handleUpdate}
                                variant="primary"
                                size="sm"
                                className="flex-1"
                            >
                                Update
                            </Button>
                            <Button
                                onClick={handleDismiss}
                                variant="secondary"
                                size="sm"
                                className="flex-1"
                            >
                                Later
                            </Button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default PWAUpdatePrompt